/**
 * spec-48 W2 — Setup redirect components
 *
 * Simple redirects: old /setup/* paths → /setup-registration/*
 * Query-string redirect: /setup/matters?service_line=X → /<line>/matters
 * Smart redirects: matter-detail, kanban, task-detail resolve service_line via API fetch
 */
import React, { useEffect, useState } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import axios from 'axios';
import { tokenStorage } from '../auth/authStore';

// ── Line code → module path mapping (spec-48 DEC-48-5) ──────────────────────

const SERVICE_LINE_TO_PATH: Record<string, string> = {
  transactions: 'transactions',
  setup_post_incorporation: 'post-incorporation',
  setup_registration: 'setup-registration',
  legal_services: 'legal-services',
  court: 'court',
  retainer: 'retainer',
};

function authHeaders() {
  const token = tokenStorage.getAccessToken();
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    // SEC-48-03: read real tenant_id from localStorage (not hardcoded dev UUID)
    'X-Tenant-ID': localStorage.getItem('tenant_id') ?? '11111111-1111-1111-1111-111111111111',
  };
}

// ── Shared spinner ───────────────────────────────────────────────────────────

function ResolvingSpinner() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8, gap: 2 }}>
      <CircularProgress size={28} />
      <Typography variant="body2" color="text.secondary">Redirecting…</Typography>
    </Box>
  );
}

// ── 2.3 — Query-string redirect for /setup/matters?service_line=X ────────────

/**
 * SetupServiceLineRedirect
 * Mounted at route "setup/matters" (no :id segment).
 * Reads ?service_line= and redirects to the correct module matters page.
 * Falls through to /setup-registration/matters for unknown/missing values.
 * Preserves all other query params (e.g. ?status=open).
 */
export function SetupServiceLineRedirect() {
  const [searchParams] = useSearchParams();
  const lineCode = searchParams.get('service_line') ?? '';
  const modulePath = SERVICE_LINE_TO_PATH[lineCode] ?? 'setup-registration';

  const otherParams = new URLSearchParams(searchParams);
  otherParams.delete('service_line');
  const suffix = otherParams.toString() ? `?${otherParams.toString()}` : '';

  return <Navigate to={`/${modulePath}/matters${suffix}`} replace />;
}

// ── 2.4 — Smart redirect for /setup/matters/:id ──────────────────────────────

/**
 * MatterDetailRedirect
 * Fetches the matter to discover its service_line, then redirects to
 * /<line>/matters/:id. Shows a spinner during fetch. 404 → /setup-registration/matters.
 */
export function MatterDetailRedirect() {
  const { id } = useParams<{ id: string }>();
  const [target, setTarget] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!id) { setTarget('/setup-registration/matters'); return; }
    axios
      .get(`/api/v1/setup/matters/${id}`, { headers: authHeaders() })
      .then((res) => {
        // SEC-48-01: backend now returns service_line_code as top-level field
        const lineCode: string =
          ((res.data as Record<string, unknown>)?.service_line_code as string | undefined)
          ?? 'setup_registration';
        const path = SERVICE_LINE_TO_PATH[lineCode] ?? 'setup-registration';
        setTarget(`/${path}/matters/${id}`);
      })
      .catch((err) => {
        if ((err as { response?: { status?: number } })?.response?.status === 404) setNotFound(true);
        else setTarget('/setup-registration/matters');
      });
  }, [id]);

  if (notFound) return <Navigate to="/setup-registration/matters" replace />;
  if (target) return <Navigate to={target} replace />;
  return <ResolvingSpinner />;
}

// ── 2.5 — Smart redirect for /setup/kanban/:txTypeCode ──────────────────────

/**
 * KanbanRedirect
 * Fetches the transaction type by code to discover its service_line,
 * then redirects to /<line>/kanban/:txTypeCode.
 * 'all' shortcut → /setup-registration/kanban/all (no fetch needed).
 */
export function KanbanRedirect() {
  const { txTypeCode } = useParams<{ txTypeCode: string }>();
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    if (!txTypeCode || txTypeCode === 'all') {
      setTarget('/setup-registration/kanban/all');
      return;
    }
    // SEC-48-02: use dedicated endpoint that returns service_line_code directly
    axios
      .get(`/api/v1/setup/transaction-types/by-code/${encodeURIComponent(txTypeCode)}`, {
        headers: authHeaders(),
      })
      .then((res) => {
        const lineCode: string =
          ((res.data as Record<string, unknown>)?.service_line_code as string | undefined)
          ?? 'setup_registration';
        const path = SERVICE_LINE_TO_PATH[lineCode] ?? 'setup-registration';
        setTarget(`/${path}/kanban/${txTypeCode}`);
      })
      .catch(() => {
        setTarget(`/setup-registration/kanban/${txTypeCode ?? 'all'}`);
      });
  }, [txTypeCode]);

  if (target) return <Navigate to={target} replace />;
  return <ResolvingSpinner />;
}

// ── Task-detail redirect ─────────────────────────────────────────────────────

/**
 * TaskDetailRedirect — /setup/matters/:matter_id/tasks/:task_id
 * Fetches matter to determine service_line, then redirects to
 * /<line>/matters/:matter_id/tasks/:task_id.
 */
export function TaskDetailRedirect() {
  const { matter_id, task_id } = useParams<{ matter_id: string; task_id: string }>();
  const [target, setTarget] = useState<string | null>(null);

  useEffect(() => {
    if (!matter_id || !task_id) { setTarget('/setup-registration/matters'); return; }
    axios
      .get(`/api/v1/setup/matters/${matter_id}`, { headers: authHeaders() })
      .then((res) => {
        // SEC-48-01: backend now returns service_line_code as top-level field
        const lineCode: string =
          ((res.data as Record<string, unknown>)?.service_line_code as string | undefined)
          ?? 'setup_registration';
        const path = SERVICE_LINE_TO_PATH[lineCode] ?? 'setup-registration';
        setTarget(`/${path}/matters/${matter_id}/tasks/${task_id}`);
      })
      .catch(() => {
        setTarget(`/setup-registration/matters/${matter_id}/tasks/${task_id}`);
      });
  }, [matter_id, task_id]);

  if (target) return <Navigate to={target} replace />;
  return <ResolvingSpinner />;
}
